import React, { useState, useEffect, useCallback } from "react";
import { StyleSheet, ScrollView, Platform, Text, View, Dimensions, RefreshControl } from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import {
  clearSuccessMessage,
  setSelectedFilters,
  resetShiftHandoverState,
  setSelectedShiftHandOver,
} from "../store/slices/shiftHandOverSlice";
import { formatDate } from "../utils/dateFormatter";
import { getPeriodOptions, getWeekOptions, getYearOptions } from "../utils/filterOptions";

import ShiftHandOverWelcome from "./ShiftHandOver/ShiftHandOverWelcome";
import ShiftHandOverBegin from "./ShiftHandOver/ShiftHandOverBegin";
import ShiftHandOverDetails from "./ShiftHandOver/ShiftHandOverDetails";
import ShiftHandOverMetrics from "./ShiftHandOver/ShiftHandOverMetrics";
import ShiftDetailsModal from "../components/ShiftDetailsModal";
import ShiftHandoverErrorModal from "./ShiftHandOver/components/ShiftHandoverErrorModal";
import { fetchAttendees } from "./ShiftHandOver/fetchAttendees";
import {
  fetchShiftHandoverData,
  ShiftHandoverRecord,
} from "./ShiftHandOver/fetchShiftHandoverData";
import { formatUserName } from "../utils/emailTouserName";
import MobileFilterOverlay from "../components/MobileFilterOverlay";
import { useTheme } from "../theme";

const VIEW_TYPES = {
  WELCOME: "welcome" as const,
  DETAILS: "details" as const,
};

const windowWidth = Dimensions.get("window").width;
const isIOSMobile = Platform.OS === "ios" && windowWidth < 768;

const ShiftHandOver: React.FC<{ navigateTo?: (screen: string) => void }> = ({ navigateTo }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  // Create a ref for the ScrollView to control scrolling
  const scrollViewRef = React.useRef<ScrollView>(null);

  // State for handling overlay visibility and navigation
  const [isFilterOverlayVisible, setFilterOverlayVisible] = useState(false);
  const [isMobileFilterOverlayVisible, setMobileFilterOverlayVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<"welcome" | "details">(
    "welcome"
  );
  const [fullShiftHandoverData, setFullShiftHandoverData] = useState<
    ShiftHandoverRecord[]
  >([]);
  // Flag to track if we're returning from details view and need to refresh data
  const [returnedFromDetails, setReturnedFromDetails] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // State for handover data and filters
  const [shiftHandoverData, setShiftHandoverData] = useState<
    ShiftHandoverRecord[]
  >([]);
  const [filters, setFilters] = useState({
    shift: "",
    Created_By: "",
    Modified_By: "",
    status: "",
    period: "",
    week: "",
    year: "",
  });

  // Selected handover for details view
  const [selectedHandover, setSelectedHandover] =
    useState<ShiftHandoverRecord | null>(null);
  const [isDetailsModalVisible, setDetailsModalVisible] = useState(false);

  const dispatch = useDispatch();

  // Get the selected location from the Redux store
  const selectedLocation = useSelector(
    (state: RootState) => state.appData.selectedLocation
  );
  const fieldUsers = useSelector((state: RootState) => state.appData?.FieldUsers ?? []);
  const coeUsers = useSelector((state: RootState) => state.appData?.COEUsers ?? []);

  // Function to open the filter overlay
  const openFilterOverlay = () => {
    setFilterOverlayVisible(true);
  };

  // Function to close the filter overlay
  const closeFilterOverlay = () => {
    setFilterOverlayVisible(false);
  };

  // Function to handle saving and navigation to details
  const handleSaveAndNavigate = () => {
    closeFilterOverlay();
    setCurrentView("details");

    // We don't need to change handOverState here as it is already set in the Redux store
  };
  // Define filter options - use Redux shift options if available
  const { shiftOptions } = useSelector(
    (state: RootState) => state.shiftHandOver
  );

  const filterOptions = React.useMemo(
    () => [
      {
        key: "shift",
        label: "Shift",
        selectedOptionId: filters.shift,
        options: shiftOptions?.length
          ? shiftOptions.map((option) => ({
              id: String(option.id ?? ""),
              displayText: `${option.displayText} Shift`,
            }))
          : [
              { id: "1st", displayText: "1st Shift" },
              { id: "2nd", displayText: "2nd Shift" },
              { id: "3rd", displayText: "3rd Shift" },
            ],
      },
      {
        key: "Created_By",
        label: "Created By",
        selectedOptionId: filters.Created_By,
        options: Array.from(
          new Set(
            fullShiftHandoverData
              .map((item) => item.Created_By)
              .filter(Boolean)
          )
        ).map((email) => ({
          id: String(email ?? ""),
          displayText: formatUserName(email),
        })),
      },
      {
        key: "Modified_By",
        label: "Finalized By",
        selectedOptionId: filters.Modified_By,
        options: Array.from(
          new Set(
            fullShiftHandoverData
              .map((item) => item.Modified_By)
              .filter(Boolean)
          )
        ).map((email) => ({
          id: String(email ?? ""),
          displayText: formatUserName(email ?? undefined),
        })),
      },
      {
        key: "status",
        label: "Status",
        selectedOptionId: filters.status,
        options: Array.from(
          new Set(
            fullShiftHandoverData.map((item) => item.status).filter(Boolean)
          )
        ).map((status) => ({ id: String(status ?? ""), displayText: String(status ?? "") })),
      },
      {
        key: "period",
        label: "Period",
        selectedOptionId: filters.period,
        options: getPeriodOptions().filter((option) => option.id !== "all" && option.id !== ""),
      },
      {
        key: "week",
        label: "Week",
        selectedOptionId: filters.week,
        options: getWeekOptions().filter((option) => option.id !== "all" && option.id !== ""),
      },
      {
        key: "year",
        label: "Year",
        selectedOptionId: filters.year || "",
        options: getYearOptions().map((option) => ({
          id: String(option.id),
          displayText: option.displayText,
        })),
      },
    ],
    [filters, shiftOptions, fullShiftHandoverData, getYearOptions]
  );

  // Function to handle filter changes (show name, filter by email)
  const handleFilterChange = (key: string, value: any) => {
    let filterValue = "";
    if (value && typeof value === "object") {
      if (value.displayText === "All" || !value.id) {
        filterValue = "";
      } else {
        filterValue = value.id;
      }
    } else {
      filterValue = value || "";
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: filterValue,
    }));
    dispatch(setSelectedFilters({ [key]: filterValue }));
  };

  // Filter table data based on selected filters (like SparcWalk/LeadershipWalk)
  const filteredTableData = React.useMemo(() => {
    let data = fullShiftHandoverData;
    if (filters.shift) {
      data = data.filter((item) => String(item.Shift) === String(filters.shift));
    }
    if (filters.Created_By) {
      data = data.filter((item) => item.Created_By === filters.Created_By);
    }
    if (filters.Modified_By) {
      data = data.filter((item) => item.Modified_By === filters.Modified_By);
    }
    if (filters.status) {
      data = data.filter((item) => item.status === filters.status);
    }
    if (filters.period) {
      data = data.filter((item) => String(item.Period) === String(filters.period));
    }
    if (filters.week) {
      data = data.filter((item) => String(item.Week) === String(filters.week));
    }
    if (filters.year) {
      data = data.filter((item) => {
        // LeadershipWalk/SparcWalk: year is usually item.year (number or string)
        // Fallback to item.Year, item.Period_Week_Year, or extract from Created/Modified date
        let yearVal = "";
        if ((item as any).year !== undefined && (item as any).year !== null) yearVal = String((item as any).year);
        else if ((item as any).Year !== undefined && (item as any).Year !== null) yearVal = String((item as any).Year);
        else if ((item as any).Period_Week_Year !== undefined && (item as any).Period_Week_Year !== null) yearVal = String((item as any).Period_Week_Year);
        else if (item.Created) yearVal = String(new Date(item.Created).getFullYear());
        else if (item.Modified) yearVal = String(new Date(item.Modified).getFullYear());
        return yearVal === String(filters.year);
      });
    }
    return data;
  }, [fullShiftHandoverData, filters]);

    // Function to fetch handover data with applied filters
  const fetchHandoverData = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await fetchShiftHandoverData({
        setIsLoading,
        setApiError,
        dispatch,
        selectedLocation,
        selectedFilters: filters,
      });
      console.log("Fetched handover data:", data);

      let filteredData = Array.isArray(data) ? data : [];

      // Save full data for filter dropdowns (unfiltered)
      setFullShiftHandoverData(filteredData);

      // Apply local filtering
      if (filters.Created_By) {
        filteredData = filteredData.filter(
          (item) => item.Created_By === filters.Created_By
        );
      }
      if (filters.Modified_By) {
        filteredData = filteredData.filter(
          (item) => item.Modified_By === filters.Modified_By
        );
      }
      if (filters.status) {
        filteredData = filteredData.filter(
          (item) => item.status === filters.status
        );
      }
      if (filters.period) {
        filteredData = filteredData.filter(
          (item) => String(item.Period) === filters.period
        );
      }
      if (filters.week) {
        filteredData = filteredData.filter(
          (item) => String(item.Week) === filters.week
        );
      }

      setShiftHandoverData(filteredData);
    } catch (error) {
      console.error("Error in fetchHandoverData:", error);
      setShiftHandoverData([]);
      setFullShiftHandoverData([]); // Clear in case of error
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, selectedLocation, filters]);
  
  // Auto-apply filters by triggering API call (like Observations) instead of client-side filtering
  React.useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (selectedLocation) {
        fetchHandoverData();
      }
    }, 250);

    return () => clearTimeout(debounceTimer);
  }, [filters, selectedLocation, fetchHandoverData]);

  // Use server-filtered data directly
  const dataWithPeriodWeek = React.useMemo(() => {
    return shiftHandoverData.map(item => ({
      ...item,
      'Period_Week': item.Period && item.Week ? 
        `P${item.Period}W${item.Week}` : 
        item.Period ? `P${item.Period}` : 
        item.Week ? `W${item.Week}` : "-"
    }));
  }, [shiftHandoverData]);

  const tableColumns = [
    { columnKey: "Shift", header: "Shift" },
    {
      columnKey: "Attendees",
      header: "Attendees",
      formatter: (value: any[] | string | null) => {
        if (!value) return "-";
        if (typeof value === "string") {
          const attendeeEmails = value.split(",").map((email) => email.trim());
          if (attendeeEmails.length === 0) return "-";
          const maxNamesToDisplay = 2;
          const displayNames = attendeeEmails
            .slice(0, maxNamesToDisplay)
            .map((email) => formatUserName(email));
          if (attendeeEmails.length > maxNamesToDisplay) {
            return `${displayNames.join(", ")} +${
              attendeeEmails.length - maxNamesToDisplay
            } more`;
          }
          return displayNames.join(", ");
        }
        if (Array.isArray(value) && value.length > 0) {
          const maxNamesToDisplay = 2;
          const displayNames = value
            .slice(0, maxNamesToDisplay)
            .map(
              (attendee) =>
                attendee.displayName ||
                attendee.name ||
                attendee.id ||
                "Unknown"
            );
          if (value.length > maxNamesToDisplay) {
            return `${displayNames.join(", ")} +${
              value.length - maxNamesToDisplay
            } more`;
          }
          return displayNames.join(", ");
        }
        return "-";
      },
    },
    {
      columnKey: "Created",
      header: "Created Time",
      formatter: (value: string) => formatDate(value),
    },
    {
      columnKey: "Created_By",
      header: "Created By",
      formatter: (value: string | null) => formatUserName(value ?? undefined),
    },
    {
      columnKey: "Modified",
      header: "Finalized Time",
      formatter: (value: string) => formatDate(value),
    },
    {
      columnKey: "Modified_By",
      header: "Finalized By",
      formatter: (value: string | null) => formatUserName(value ?? undefined),
    },
    {
      columnKey: "Period_Week",
      header: "Period Week",
      formatter: (value: string | null) => formatUserName(value ?? undefined),
    },
    { columnKey: "status", header: "Status" },
  ];

  // Function to handle row click in data table
  const handleRowClick = (
    rowData: ShiftHandoverRecord & {
      isShiftHandOverNavigation?: boolean;
      columnKey?: string;
    }
  ) => {
    console.log("Selected handover record:", rowData);
    console.log("Column key:", rowData.columnKey);
    console.log(
      "isShiftHandOverNavigation:",
      rowData.isShiftHandOverNavigation
    );

    // Convert to ShiftHandOverRowData format and store in Redux
    const rowDataForRedux = {
      ID: rowData.ID,
      Title: rowData.Title,
      Attendees: rowData.Attendees || [],
      Notes: rowData.Notes,
      Location_Name: rowData.Location_Name,
      Location_Name_Loc_ID: null, // Default value
      Walk_Type: rowData.Walk_Type,
      Zone: null, // Default value
      Frequency: null, // Default value
      Created: rowData.Created,
      Period: rowData.Period || 0,
      Week: rowData.Week || 0,
      Shift: rowData.Shift || null,
      SPARC_Category_Selected: null, // Default value
      Walk_Number: rowData.Walk_Number,
      Modified: rowData.Modified,
      Created_By: rowData.Created_By,
      Modified_By: rowData.Modified_By,
      status: rowData.status || "unknown",
    };

    dispatch(setSelectedShiftHandOver(rowDataForRedux));

    // If the click is on the Shift column, open the details form
    if (rowData.columnKey === "Shift" || rowData.isShiftHandOverNavigation) {
      console.log("Opening details view");
      setSelectedHandover(rowData);
      setCurrentView(VIEW_TYPES.DETAILS);
    } else {
      // For any other cell, open the overlay modal
      console.log("Opening overlay modal");
      setSelectedHandover(rowData);
      setDetailsModalVisible(true);
    }
  };

  // Function to close the details modal
  const closeDetailsModal = () => {
    setDetailsModalVisible(false);
    setSelectedHandover(null);
  };

  // Fetch attendees when component mounts
  useEffect(() => {
    fetchAttendees({
      setIsLoading,
      setApiError,
      dispatch,
      fieldUsers,
      coeUsers,
    });
  }, [dispatch, fieldUsers, coeUsers]);

  // Fetch handover data only when location changes or when returning to welcome view
  useEffect(() => {
    if (selectedLocation) {
      // Initial data load with empty filters
      const initialLoad = async () => {
        try {
          setIsLoading(true);
          const data = await fetchShiftHandoverData({
            setIsLoading,
            setApiError,
            dispatch,
            selectedLocation,
            selectedFilters: {
              shift: "",
              Created_By: "",
              Modified_By: "",
              status: "",
            },
          });

          // Set both the filtered and full data sets
          const handoverData = Array.isArray(data) ? data : [];
          setShiftHandoverData(handoverData);
          setFullShiftHandoverData(handoverData); // Make sure to set the full data for filters
        } catch (error) {
          console.error("Error in initial data load:", error);
          setShiftHandoverData([]);
          setFullShiftHandoverData([]); // Clear both data sets in case of error
        } finally {
          setIsLoading(false);
        }
      };
      initialLoad();
    }
  }, [selectedLocation, dispatch]); // Removed currentView from dependencies

  // Get success message from Redux state
  const { successMessage } = useSelector(
    (state: RootState) => state.shiftHandOver
  );
  // Effect to refresh data when returning from details view
  useEffect(() => {
    if (returnedFromDetails && currentView === "welcome") {
      // Fetch fresh data from API
      fetchHandoverData();

      // Reset the flag
      setReturnedFromDetails(false);
    }
  }, [returnedFromDetails, currentView, fetchHandoverData]);

  // Clear success message after 5 seconds and scroll to top when there's a message
  useEffect(() => {
    if (successMessage) {
      // Scroll to top when success message appears
      if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({ y: 0, animated: true });
      }

      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage());
      }, 5000); // 5 seconds
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  // Function to reset filters and reload data without filters
  const resetFilters = useCallback(async () => {
    try {
      setIsLoading(true);
      // Reset all filter state to default values
      const defaultFilters = {
        shift: "",
        Created_By: "",
        Modified_By: "",
        status: "",
        period: "",
        week: "",
        year: "",
      };
      setFilters(defaultFilters);
      // Fetch handover data without any filters
      const data = await fetchShiftHandoverData({
        setIsLoading,
        setApiError,
        dispatch,
        selectedLocation,
        selectedFilters: defaultFilters,
      });
      setShiftHandoverData(data);
      setFullShiftHandoverData(data);
    } catch (err) {
      console.error("Error resetting filters:", err);
      setApiError(
        err instanceof Error ? err.message : "Failed to reset filters"
      );
      setShiftHandoverData([]);
      setFullShiftHandoverData([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocation, dispatch, setIsLoading, setApiError]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    const timeout = new Promise<void>((_, reject) =>
      setTimeout(() => reject(new Error('refresh_timeout')), 3000)
    );
    try {
      await Promise.race([fetchHandoverData(), timeout]);
    } catch {
      // timeout or error — silently cancel
    } finally {
      setRefreshing(false);
    }
  }, [fetchHandoverData]);

  const handleMobileFilterPress = () => {
    setMobileFilterOverlayVisible(true);
  };
  const handleMobileFilterCancel = () => {
    setMobileFilterOverlayVisible(false);
  };
  const handleMobileFilterProceed = async (nextFilters = {}) => {
    // Normalize and apply selected filters
    Object.entries(nextFilters).forEach(([key, value]) => {
      handleFilterChange(key, value);
    });
    setMobileFilterOverlayVisible(false);
  };

  return (
    <>
      {currentView === VIEW_TYPES.DETAILS ? (
        <ShiftHandOverDetails
          isVisible={true}
          selectedHandover={selectedHandover}
          onBackPress={() => {
            dispatch(resetShiftHandoverState());
            setSelectedHandover(null);
            setReturnedFromDetails(true);
            setCurrentView("welcome");
            if (successMessage) {
              setTimeout(() => {
                if (scrollViewRef.current) {
                  scrollViewRef.current.scrollTo({ y: 0, animated: true });
                }
              }, 100);
            }
          }}
          onHomePress={() => {
            dispatch(resetShiftHandoverState());
            setSelectedHandover(null);
            if (navigateTo) {
              navigateTo("Home");
            } else {
              setCurrentView("welcome");
            }
          }}
        />
      ) : (
        // Mobile overlay: show only overlay if visible
        isIOSMobile && isMobileFilterOverlayVisible ? (
          <MobileFilterOverlay
            visible={isMobileFilterOverlayVisible}
            onDismiss={handleMobileFilterCancel}
            filters={filterOptions}
            onFilterChange={handleFilterChange}
            onProceed={handleMobileFilterProceed}
            onCancel={handleMobileFilterCancel}
            currentFilters={filters}
          />
        ) : (
          <ScrollView
            ref={scrollViewRef}
            style={[
              styles.container,
              { backgroundColor: colors.background.page },
            ]}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                tintColor={colors.brand.primary}
                colors={[colors.brand.primary]}
                title="Refreshing..."
                titleColor={isDarkMode ? colors.text.primary : colors.brand.primary}
              />
            }
          >
          {/* Display success message if available */}
          {successMessage && (
            <View
              style={[
                styles.successMessageContainer,
                isDarkMode && {
                  backgroundColor: colors.background.surface,
                  borderColor: colors.border.default,
                },
              ]}
            >
              <MaterialIcons
                name="check-circle"
                size={24}
                color="#008242"
                style={styles.successIcon}
              />
              <Text
                style={[
                  styles.successMessageText,
                  isDarkMode && { color: colors.text.primary },
                ]}
              >
                {successMessage}
              </Text>
            </View>
          )}

          {/* Display global API error if any */}
          {apiError && !isLoading && !currentView.includes("details") && (
            <View
              style={[
                styles.errorContainer,
                isDarkMode && {
                  backgroundColor: colors.background.surface,
                  borderColor: colors.border.default,
                },
              ]}
            >
              <Text style={[styles.errorText, isDarkMode && { color: colors.text.primary }]}>Error: {apiError}</Text>
            </View>
          )}

          {/* Conditionally render welcome or details view */}
          {currentView === VIEW_TYPES.WELCOME && (
            <>
              <ShiftHandOverWelcome openFilterOverlay={openFilterOverlay} />

              {/* Metrics section with filters and data table */}
              <View style={styles.metricsContainer}>
                <ShiftHandOverMetrics
                  filterOptions={filterOptions}
                  handleFilterChange={handleFilterChange}
                  isLoading={isLoading}
                  apiError={apiError}
                  handoverData={shiftHandoverData.map(item => ({
                    ...item,
                    'Period_Week': item.Period && item.Week ? 
                      `P${item.Period}W${item.Week}` : 
                      item.Period ? `P${item.Period}` : 
                      item.Week ? `W${item.Week}` : "-"
                  }))}
                  tableColumns={tableColumns}
                  handleRowClick={handleRowClick}
                  resetFilters={resetFilters}
                  showMobileFilterButton={isIOSMobile}
                  onMobileFilterPress={handleMobileFilterPress}
                />
              </View>
            </>
          )}

          {/* Overlay component with shift selector, attendees dropdown, and save/cancel buttons */}
          <ShiftHandOverBegin
            isFilterOverlayVisible={isFilterOverlayVisible}
            closeFilterOverlay={closeFilterOverlay}
            onSave={handleSaveAndNavigate}
          />

          {/* Shift Details Modal */}
          <ShiftDetailsModal
            selectedRowData={selectedHandover}
            isOverlayVisible={isDetailsModalVisible}
            closeOverlay={closeDetailsModal}
          />
        </ScrollView>
      )
    )
    }
    
    {/* Centralized Error Modal - Always rendered regardless of view */}
    <ShiftHandoverErrorModal />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: Platform.OS === "web" ? "1.5%" : 16,
    backgroundColor: "#F5F9FF",
    paddingVertical: "1.5%",
  },
  successMessageContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#d4edda",
    borderColor: "#c3e6cb",
    borderWidth: 1,
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
  },
  successIcon: {
    marginRight: 8,
  },
  successMessageText: {
    color: "#155724",
    fontSize: 16,
    flex: 1,
  },
  errorContainer: {
    backgroundColor: "#f8d7da",
    borderColor: "#f5c6cb",
    borderWidth: 1,
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
  },
  errorText: {
    color: "#dc3545",
    fontSize: 16,
    padding: 10,
    marginBottom: 10,
  },
  loadingText: {
    color: "#0066CC",
    fontSize: 16,
    padding: 10,
    marginBottom: 10,
  },
  metricsContainer: {
    marginTop: 20,
    marginBottom: 20,
  },
});

export default ShiftHandOver;
